import type {
  CraftResponse,
  FarmResponse,
  FishingResponse,
  FlowsResponse,
  GoalRequest,
  GoalsResponse,
  MarketResponse,
  PlanResponse,
  SellResponse,
  SkillColour,
} from '@brimstone/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

const get =
  <T>(path: string) =>
  () =>
    api<T>(path);

export const useSell = () =>
  useQuery({ queryKey: ['gold', 'sell'], queryFn: get<SellResponse>('/api/gold/sell') });
export const useCraft = () =>
  useQuery({ queryKey: ['gold', 'craft'], queryFn: get<CraftResponse>('/api/gold/craft') });
export const useFarm = () =>
  useQuery({ queryKey: ['gold', 'farm'], queryFn: get<FarmResponse>('/api/gold/farm') });
export const useFishing = () =>
  useQuery({ queryKey: ['gold', 'fishing'], queryFn: get<FishingResponse>('/api/gold/fishing') });
export const useMarket = () =>
  useQuery({ queryKey: ['gold', 'market'], queryFn: get<MarketResponse>('/api/gold/market') });
export const useFlows = () =>
  useQuery({ queryKey: ['gold', 'flows'], queryFn: get<FlowsResponse>('/api/gold/flows?weeks=4') });
export const usePlan = () =>
  useQuery({ queryKey: ['gold', 'plan'], queryFn: get<PlanResponse>('/api/gold/plan') });
export const useGoals = () =>
  useQuery({ queryKey: ['gold', 'goals'], queryFn: get<GoalsResponse>('/api/gold/goals') });

/** Create, edit or delete a goal; the response is the new list. */
export function useGoalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: { id?: string; delete?: boolean; goal?: GoalRequest }) =>
      api<GoalsResponse>(m.id ? `/api/gold/goals/${m.id}` : '/api/gold/goals', {
        method: m.delete ? 'DELETE' : m.id ? 'PUT' : 'POST',
        json: m.delete ? undefined : m.goal,
      }),
    onSuccess: (data) => qc.setQueryData(['gold', 'goals'], data),
  });
}

/** A recipe's colour as a status word, with a token colour (no ad-hoc colours: DESIGN). */
export const COLOURS: Record<SkillColour, { word: string; className: string }> = {
  orange: { word: 'ORANGE', className: 'text-accent' },
  yellow: { word: 'YELLOW', className: 'text-text' },
  green: { word: 'GREEN', className: 'text-muted' },
  grey: { word: 'GREY', className: 'text-muted opacity-60' },
};
